import { motion } from "framer-motion";
import { Play, Radio, Tv2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useImages } from "../../lib/ImagesContext";
import { useContent } from "../../lib/ContentContext";

const FRAME_META = [
  { slot: "tvfan-1", tag: "STUDIO A · LIVE" },
  { slot: "tvfan-2", tag: "AIRPORT · MUM T2" },
  { slot: "tvfan-3", tag: "MALL · GURUGRAM" },
  { slot: "tvfan-4", tag: "CINEMA · DELHI" },
  { slot: "tvfan-5", tag: "HOTEL · BLR" },
  { slot: "tvfan-6", tag: "STUDIO B · DELHI" },
  { slot: "tvfan-7", tag: "F&B · MUMBAI" },
];

/**
 * Signature arc/fanned TV screens showcase — highlights XMLOOPS TV network.
 */
export default function TVFan() {
  const { getImg } = useImages();
  const { getText } = useContent();
  const CHANNEL_FRAMES = FRAME_META.map((f) => ({ src: getImg(f.slot), tag: f.tag }));
  const half = (CHANNEL_FRAMES.length - 1) / 2;

  return (
    <section
      className="relative overflow-hidden bg-black py-24 md:py-36"
      data-testid="tv-fan-section"
    >
      {/* Signature crimson beam through center */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[3px] h-[80%] bg-gradient-to-b from-transparent via-[#e63946] to-transparent opacity-70 blur-[2px]" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-[#e63946] opacity-[0.12] blur-[180px]" />
      </div>

      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto">
          <div className="flex justify-center">
            <div className="tag-glass">
              <Radio className="w-3 h-3" /> {getText("tvfan.tag")}
            </div>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="font-display text-white text-4xl md:text-6xl leading-[1.02] tracking-tight mt-6"
          >
            {getText("tvfan.headline_a")} <br />
            <span className="italic-accent text-grad-crimson">{getText("tvfan.headline_b")}</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-white/72 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mt-6"
          >
            Airports, five-star hotels, luxury malls, cinemas, fine-dining and
            executive workspaces — all broadcasting from a single owned network.
            The screens we control are the screens your audience already trusts.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-10 inline-flex"
          >
            <Link
              to={getText("tvfan.cta_url")}
              className="btn btn-primary"
              data-testid="tv-fan-cta"
            >
              <Play className="w-4 h-4 fill-white" />
              {getText("tvfan.cta_label")}
            </Link>
          </motion.div>
        </div>

        {/* Arc / fan gallery */}
        <div className="relative mt-16 md:mt-20 h-[380px] md:h-[520px]">
          <div className="absolute inset-x-0 bottom-0 flex justify-center items-end">
            {CHANNEL_FRAMES.map((f, i) => {
              const offset = i - half;
              const rotate = offset * 9;
              const translateY = Math.abs(offset) * 30;
              const translateX = offset * 145;
              const z = 20 - Math.abs(offset);
              const scale = 1 - Math.abs(offset) * 0.03;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: translateX, y: translateY + 80, rotate: 0, scale }}
                  whileInView={{ opacity: 1, x: translateX, y: translateY, rotate, scale }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.9, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
                  style={{ zIndex: z, transformOrigin: "bottom center" }}
                  className="arc-card absolute bottom-0 w-[150px] md:w-[210px] aspect-[3/4] rounded-xl overflow-hidden bg-neutral-900"
                  data-testid={`tv-fan-card-${i}`}
                >
                  <img src={f.src} alt={f.tag} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                  <div className="absolute inset-0 pointer-events-none"
                       style={{
                         backgroundImage: "repeating-linear-gradient(0deg, rgba(0,0,0,0.15) 0 2px, transparent 2px 3px)",
                         mixBlendMode: "overlay",
                       }} />
                  <div className="absolute top-2 left-2 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#e63946] animate-pulse" />
                    <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-white/90">On air</span>
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <div className="font-mono text-[8px] md:text-[9px] uppercase tracking-[0.22em] text-white/85 truncate">
                      {f.tag}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Feature triplet under the fan */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-24">
          {[
            {
              icon: <Tv2 className="w-5 h-5" />,
              title: "24 / 7 broadcast",
              body: "Continuous premium programming across news, lifestyle, motorsport and business — every slot is inventory we own.",
            },
            {
              icon: <Radio className="w-5 h-5" />,
              title: "320+ owned screens",
              body: "Curated placements across airports, malls, hotels and cinemas where India&rsquo;s decision-makers already spend time.",
            },
            {
              icon: <Play className="w-5 h-5" />,
              title: "Broadcast-grade production",
              body: "TVCs, brand films, live shoots and post-production — created in-house and delivered to network standards.",
            },
          ].map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="p-8 rounded-2xl border border-white/10 bg-white/[0.02] hover:border-[#e63946]/50 transition-colors"
              data-testid={`tv-feature-${i}`}
            >
              <div className="w-10 h-10 rounded-xl bg-[#e63946]/15 border border-[#e63946]/30 text-[#ff8a90] flex items-center justify-center">
                {f.icon}
              </div>
              <h3 className="font-display text-white text-2xl mt-6 leading-tight tracking-tight">{f.title}</h3>
              <p className="text-white/72 mt-3 leading-relaxed"
                 dangerouslySetInnerHTML={{ __html: f.body }} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
